import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  useTable,
  usePagination,
  useGlobalFilter,
  useFilters,
  useSortBy,
} from 'react-table';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import CircularProgress from '@mui/material/CircularProgress';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TableViewIcon from '@mui/icons-material/TableView';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../../pages/Fees/Fees.css';
import '../../pages/Fees/FeeConcessions.css';
import FeePlannerModal from './FeePlannerModal';
import FeeConcessions from './FeeConcessions';
import configData from '../../configure';
import { useAuth } from '../../AuthContext';

// -------------------------------------------------
// CONSTANTS
// -------------------------------------------------
const CURRENT_EDITABLE_YEAR = '2025-2026';
const VIEW_ONLY_YEAR = '2024-2025';
const ACADEMIC_YEARS = ['2024-2025', '2025-2026', '2026-2027'];

// -------------------------------------------------
// MAIN COMPONENT
// -------------------------------------------------
function FeeDetails({ orgId, initialTab = 0 }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');

  // Sync activeTab with URL ?tab= search param or initialTab prop
  const activeTab = useMemo(() => {
    if (tabParam === 'concessions' || tabParam === '1') return 1;
    if (tabParam === 'planning' || tabParam === '0') return 0;
    return initialTab;
  }, [tabParam, initialTab]);

  const handleTabChange = useCallback((event, newValue) => {
    const tabName = newValue === 1 ? 'concessions' : 'planning';
    setSearchParams({ tab: tabName }, { replace: true });
  }, [setSearchParams]);

  // ---------- STATE ----------
  const [feeStructure, setFeeStructure] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedAcademicYear, setSelectedAcademicYear] = useState(
    CURRENT_EDITABLE_YEAR
  );
  const [openModal, setOpenModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentFeeStructureId, setCurrentFeeStructureId] = useState(null);
  const [currentFeePlanId, setCurrentFeePlanId] = useState(null);
  const [loadingClasses, setLoadingClasses] = useState(true);
  const [classOptions, setClassOptions] = useState([]);

  const { axios: authAxios, token, user, parentOrgId } = useAuth();
  const effectiveParentOrgId =
    parentOrgId ||
    user?.parentOrgId ||
    user?.orgDetails?.parentOrgId ||
    sessionStorage.getItem('parentOrgId') ||
    localStorage.getItem('parentOrgId') ||
    '';
  const isSmallOrg = effectiveParentOrgId === 'SMALLORG-001';

  // ---------- HELPERS ----------
  const parseISOToLocalDate = useCallback(iso => (iso ? iso.split('T')[0] : ''), []);
  const IS_VIEW_ONLY_YEAR = selectedAcademicYear === VIEW_ONLY_YEAR;

  // ---------- FETCH CLASSES ----------
  useEffect(() => {
    const fetchClassOptions = async () => {
      if (!orgId) return;
      try {
        setLoadingClasses(true);
        const { data } = await authAxios.get(
          `${configData.GET_CLASSES_BY_ORG}/${orgId}/metadata`
        );
        const options = data
          .map(cls => {
            const section =
              cls.sectionName && cls.sectionName !== 'null' && cls.sectionName !== 'undefined'
                ? String(cls.sectionName).trim()
                : '';
            return {
              value: cls.classId,
              label: section ? `${cls.className} ${section}` : `${cls.className || ''}`.trim(),
            };
          })
          .sort((a, b) => {
            const getInfo = l => {
              const m = l.match(/Class (\d+)\s*(\w+)/i);
              return m ? { num: +m[1], sec: m[2].toUpperCase() } : { num: 0, sec: l };
            };
            const A = getInfo(a.label),
              B = getInfo(b.label);
            return A.num !== B.num ? A.num - B.num : A.sec.localeCompare(B.sec);
          });
        setClassOptions(options);
        if (options.length && !selectedClass) setSelectedClass(options[0].value);
      } catch (e) {
        console.error(e);
        toast.error('Failed to load classes.');
      } finally {
        setLoadingClasses(false);
      }
    };
    fetchClassOptions();
  }, [orgId, authAxios]);

  // ---------- FETCH FEE STRUCTURE ----------
  useEffect(() => {
    if (!selectedClass || !selectedAcademicYear) return;
    authAxios
      .get(
        `${configData.GET_FEE_STRUCTURE}/class/${selectedClass}?academicYear=${selectedAcademicYear}`,
        { headers: { Authorization: `Bearer ${token}`, Accept: 'application/hal+json' } }
      )
      .then(res => {
        const raw = Array.isArray(res.data) ? res.data : (res.data?._embedded?.feeStructures || res.data?.items || (res.data ? [res.data] : []));
        const feeData = raw.find(i => i && i.academicYear === selectedAcademicYear) || raw[0] || null;
        setFeeStructure(feeData?.feeInstallments || []);
        setCurrentFeeStructureId(feeData?.feeStructureId || null);
        setCurrentFeePlanId(feeData?.feePlanId || null);
      })
      .catch(err => {
        console.error(err);
        setFeeStructure([]);
        setCurrentFeeStructureId(null);
        setCurrentFeePlanId(null);
      });
  }, [authAxios, token, selectedClass, selectedAcademicYear]);

  // ---------- TABLE COLUMNS ----------
  const columns = useMemo(() => {
    if (isSmallOrg) {
      return [
        { Header: 'Name', accessor: 'description' },
        {
          Header: 'Start Date',
          accessor: 'startDate',
          Cell: ({ value }) => (value ? parseISOToLocalDate(value) : ''),
        },
        {
          Header: 'End Date',
          accessor: 'endDate',
          Cell: ({ value }) => (value ? parseISOToLocalDate(value) : ''),
        },
        {
          Header: 'Due Date',
          accessor: 'dueDate',
          Cell: ({ value }) => (value ? parseISOToLocalDate(value) : ''),
        },
        {
          Header: 'Fee',
          accessor: row => {
            if (row.feeBreakdown?.Fee !== undefined && row.feeBreakdown?.Fee !== null) {
              return row.feeBreakdown.Fee;
            }
            if (row.totalFee !== undefined && row.totalFee !== null) {
              return row.totalFee;
            }
            if (row.feeBreakdown && Object.keys(row.feeBreakdown).length > 0) {
              return Object.values(row.feeBreakdown).reduce((a, b) => Number(a || 0) + Number(b || 0), 0);
            }
            return '';
          },
          Cell: ({ value }) =>
            value !== undefined && value !== null && value !== ''
              ? `₹${Number(value).toLocaleString('en-IN')}`
              : '',
        },
      ];
    }

    return [
      { Header: 'Name', accessor: 'description' },
      {
        Header: 'Start Date',
        accessor: 'startDate',
        Cell: ({ value }) => (value ? parseISOToLocalDate(value) : ''),
      },
      {
        Header: 'End Date',
        accessor: 'endDate',
        Cell: ({ value }) => (value ? parseISOToLocalDate(value) : ''),
      },
      {
        Header: 'Due Date',
        accessor: 'dueDate',
        Cell: ({ value }) => (value ? parseISOToLocalDate(value) : ''),
      },
      { Header: 'Admission Fee', accessor: 'feeBreakdown.Admission Fee' },
      { Header: 'Tuition Fee', accessor: 'feeBreakdown.Tuition Fee' },
      { Header: 'SmartClass Fee', accessor: 'feeBreakdown.SmartClass Fee' },
      { Header: 'Computer Fee', accessor: 'feeBreakdown.Computer Fee' },
      { Header: 'Sports Fee', accessor: 'feeBreakdown.Sports Fee' },
      { Header: 'Library Fee', accessor: 'feeBreakdown.Library Fee' },
      { Header: 'Extracurricular Fee', accessor: 'feeBreakdown.Extracurricular Fee' },
      { Header: 'Examination Fee', accessor: 'feeBreakdown.Examination Fee' },
      {
        Header: 'Total Fee',
        accessor: 'totalFee',
        Cell: ({ value }) => (value ?? ''),
      },
    ];
  }, [isSmallOrg, parseISOToLocalDate]);

  // ---------- AUTO-GENERATE ANNUAL ROW ----------
  const data = useMemo(() => {
    const terms = feeStructure.filter(i => !i.isAnnual);
    if (!terms.length) return terms;

    const cats = isSmallOrg
      ? ['Fee']
      : [
          'Admission Fee',
          'Tuition Fee',
          'SmartClass Fee',
          'Computer Fee',
          'Sports Fee',
          'Library Fee',
          'Extracurricular Fee',
          'Examination Fee',
        ];

    const annual = {};
    cats.forEach(c => {
      annual[c] = terms.reduce((s, t) => {
        if (isSmallOrg) {
          const val =
            t.feeBreakdown?.Fee ??
            (t.totalFee ||
              (t.feeBreakdown
                ? Object.values(t.feeBreakdown).reduce((a, b) => Number(a || 0) + Number(b || 0), 0)
                : 0));
          return s + (Number(val) || 0);
        }
        return s + (Number(t.feeBreakdown?.[c]) || 0);
      }, 0);
    });

    const totalCats = Object.values(annual).reduce((s, v) => s + Number(v || 0), 0);
    const totalTerms = terms.reduce(
      (s, t) =>
        s +
        Number(
          t.totalFee ||
            t.totalAmount ||
            t.amount ||
            (t.feeBreakdown
              ? Object.values(t.feeBreakdown).reduce((a, b) => Number(a || 0) + Number(b || 0), 0)
              : 0) ||
            0
        ),
      0
    );
    const total = totalCats > 0 ? totalCats : totalTerms;
    const start = terms.map(t => new Date(t.startDate)).filter(d => !isNaN(d));
    const end = terms.map(t => new Date(t.endDate)).filter(d => !isNaN(d));
    const due = terms.map(t => new Date(t.dueDate)).filter(d => !isNaN(d));

    const annualRow = {
      description: 'Total Annual Fee',
      startDate: start.length ? new Date(Math.min(...start)).toISOString() : '',
      endDate: end.length ? new Date(Math.max(...end)).toISOString() : '',
      dueDate: due.length
        ? new Date(new Date(Math.min(...due)).getTime() + 15 * 24 * 60 * 60 * 1000).toISOString()
        : '',
      feeBreakdown: annual,
      totalFee: total,
      isAnnual: true,
    };

    return [...terms, annualRow];
  }, [feeStructure, isSmallOrg]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
  } = useTable(
    { columns, data, initialState: { pageIndex: 0 } },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  // ---------- MODAL HANDLERS ----------
  const handleOpenModal = useCallback(() => {
    setEditMode(false);
    setOpenModal(true);
  }, []);

  const handleEditFeeStructure = useCallback(() => {
    if (IS_VIEW_ONLY_YEAR) {
      toast.error(`Fee structure for ${selectedAcademicYear} is view-only.`);
      return;
    }
    setEditMode(true);
    setOpenModal(true);
  }, [IS_VIEW_ONLY_YEAR, selectedAcademicYear]);

  const handleDeleteFeeStructure = useCallback(() => {
    if (IS_VIEW_ONLY_YEAR) {
      toast.error(`Cannot delete fee structure for ${selectedAcademicYear}.`);
      return;
    }
    if (!currentFeeStructureId) return toast.error('Nothing to delete.');
    if (!window.confirm(`Delete fee structure for ${selectedAcademicYear}?`)) return;

    authAxios
      .delete(`${configData.GET_FEE_STRUCTURE}/${currentFeeStructureId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        // HTTP 204 returns no body
        toast.success('Fee structure deleted successfully.');
        setFeeStructure([]);
        setCurrentFeeStructureId(null);
        setCurrentFeePlanId(null);
      })
      .catch((err) => {
        console.error(err);
        toast.error('Delete failed.');
      });
  }, [IS_VIEW_ONLY_YEAR, selectedAcademicYear, currentFeeStructureId, authAxios, token]);

  const handleCloseModal = useCallback(() => {
    setOpenModal(false);
    setEditMode(false);
  }, []);

  const handleSaveSuccess = useCallback(
    async classesSaved => {
      if (classesSaved.includes(selectedClass)) {
        try {
          const res = await authAxios.get(
            `${configData.GET_FEE_STRUCTURE}/class/${selectedClass}?academicYear=${selectedAcademicYear}`,
            { headers: { Authorization: `Bearer ${token}`, Accept: 'application/hal+json' } }
          );
          const raw = Array.isArray(res.data) ? res.data : (res.data?._embedded?.feeStructures || res.data?.items || (res.data ? [res.data] : []));
          const d = raw.find(i => i && i.academicYear === selectedAcademicYear) || raw[0] || null;
          setFeeStructure(d?.feeInstallments || []);
          setCurrentFeeStructureId(d?.feeStructureId || null);
          setCurrentFeePlanId(d?.feePlanId || null);
        } catch (e) {
          console.error(e);
        }
      }
    },
    [selectedClass, authAxios, selectedAcademicYear, token]
  );

  // -------------------------------------------------
  // RENDER
  // -------------------------------------------------
  return (
    <div className="wrapFees">
      <div className="fees">
        {/* Navigation Tabs */}
        <Box style={{ borderBottom: '1px solid #e2e8f0', padding: '0 24px', backgroundColor: '#ffffff' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            textColor="primary"
            indicatorColor="primary"
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '15px',
                minHeight: '48px',
                color: '#64748b',
                '&.Mui-selected': {
                  color: '#014486',
                  fontWeight: 700
                }
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#014486',
                height: '3px',
                borderRadius: '3px 3px 0 0'
              }
            }}
          >
            <Tab label="Fee Structure & Planning" icon={<TableViewIcon />} iconPosition="start" />
            <Tab label="Fee Concessions" icon={<AccountBalanceWalletIcon />} iconPosition="start" />
          </Tabs>
        </Box>

        {activeTab === 0 ? (
          <div>
            {/* ---------- TOP SELECTORS ---------- */}
            <div className="concession-toolbar" style={{ margin: '20px 2.5% 24px 2.5%', width: '95%' }}>
              <div className="concession-filters-left">
                <div className="concession-filter-group">
                  <label>Grade / Class</label>
                  {loadingClasses ? (
                    <CircularProgress size={20} />
                  ) : (
                    <Select
                      className="concession-select"
                      size="small"
                      value={selectedClass}
                      onChange={e => setSelectedClass(e.target.value)}
                    >
                      {classOptions.map(o => (
                        <MenuItem key={o.value} value={o.value}>
                          {o.label}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                </div>

                <div className="concession-filter-group">
                  <label>Academic Year</label>
                  <Select
                    className="concession-select"
                    size="small"
                    value={selectedAcademicYear}
                    onChange={e => setSelectedAcademicYear(e.target.value)}
                  >
                    {ACADEMIC_YEARS.map(y => (
                      <MenuItem key={y} value={y}>
                        {y}
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              </div>

              <div className="right-section">
                <div className="button-group">
                  {/* ADD BUTTON – only when no structure exists AND not view-only */}
                  {selectedClass && feeStructure.length === 0 && !IS_VIEW_ONLY_YEAR && (
                    <Button
                      className="stuentbutton"
                      onClick={handleOpenModal}
                      startIcon={<AddIcon />}
                    />
                  )}

                  {/* BULK IMPORT BUTTON */}
                  {!IS_VIEW_ONLY_YEAR && (
                    <Button
                      className="stuentbutton"
                      onClick={() => {
                        if (!selectedClass) {
                          toast.error('Please select a class first');
                          return;
                        }
                        navigate('/finance/bulk-import', { state: { selectedClass } });
                      }}
                      startIcon={<UploadFileIcon />}
                      title="Bulk Import Fee Plans"
                    />
                  )}

                  {/* EDIT + DELETE – only when structure exists AND not view-only */}
                  {selectedClass && feeStructure.length > 0 && (
                    <>
                      {!IS_VIEW_ONLY_YEAR && (
                        <Button
                          className="stuentbutton"
                          startIcon={<EditIcon />}
                          onClick={handleEditFeeStructure}
                        />
                      )}
                      {!IS_VIEW_ONLY_YEAR && (
                        <Button
                          className="stuentbutton"
                          color="error"
                          startIcon={<DeleteIcon />}
                          onClick={handleDeleteFeeStructure}
                        />
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* ---------- TABLE ---------- */}
            {selectedClass && data.length ? (
              <table className="tableFees" {...getTableProps()}>
                <thead>
                  {headerGroups.map(hg => (
                    <tr {...hg.getHeaderGroupProps()} key={hg.id}>
                      {hg.headers.map(col => (
                        <th
                          {...col.getHeaderProps()}
                          style={{
                            textAlign: col.id === 'Name' ? 'left' : 'center',
                          }}
                          key={col.id}
                        >
                          {col.render('Header')}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                  {page.map(row => {
                    prepareRow(row);
                    const isAnnual = row.original.isAnnual;
                    return (
                      <tr
                        {...row.getRowProps()}
                        style={
                          isAnnual
                            ? { fontWeight: 'bold', backgroundColor: '#f9f9f9' }
                            : {}
                        }
                        key={row.id}
                      >
                        {row.cells.map(cell => (
                          <td
                            {...cell.getCellProps()}
                            style={{ textAlign: 'left', paddingLeft: '10px' }}
                            key={cell.column.id}
                          >
                            {cell.render('Cell')}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : selectedClass ? (
              <p style={{ padding: '20px', color: '#64748b' }}>No fee structure for {selectedAcademicYear}.</p>
            ) : (
              <p style={{ padding: '20px', color: '#64748b' }}>Please select a class.</p>
            )}

            {/* ---------- MODAL ---------- */}
            <FeePlannerModal
              open={openModal}
              onClose={handleCloseModal}
              editMode={editMode}
              selectedClass={selectedClass}
              selectedAcademicYear={selectedAcademicYear}
              classOptions={classOptions}
              isSmallOrg={isSmallOrg}
              isViewOnlyYear={IS_VIEW_ONLY_YEAR}
              orgId={orgId}
              authAxios={authAxios}
              token={token}
              onSaveSuccess={handleSaveSuccess}
            />
          </div>
        ) : (
          <FeeConcessions orgId={orgId} isTab={true} />
        )}
      </div>
    </div>
  );
}

export default React.memo(FeeDetails);